/**
 * Sync service for offline time entries.
 * Monitors online status and syncs pending entries when connection is restored.
 */

import { api, ApiRequestError } from './api';
import {
  getPendingEntries,
  removePendingEntry,
  updatePendingEntry,
  type PendingTimeEntry,
} from './offline-store';

const MAX_SYNC_ATTEMPTS = 5;

type SyncCallback = (status: SyncStatus) => void;

export interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncAt: string | null;
  lastError: string | null;
}

class SyncService {
  private listeners: Set<SyncCallback> = new Set();
  private status: SyncStatus = {
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSyncing: false,
    pendingCount: 0,
    lastSyncAt: null,
    lastError: null,
  };
  private syncInProgress = false;
  private syncTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  /**
   * Subscribe to sync status changes.
   */
  subscribe(callback: SyncCallback): () => void {
    this.listeners.add(callback);
    callback(this.status);
    return () => this.listeners.delete(callback);
  }

  /**
   * Get current sync status.
   */
  getStatus(): SyncStatus {
    return { ...this.status };
  }

  /**
   * Manually trigger a sync.
   */
  async triggerSync(): Promise<void> {
    if (!this.status.isOnline || this.syncInProgress) {
      return;
    }
    await this.performSync();
  }

  /**
   * Update pending count (call after queueing new entry).
   */
  async refreshPendingCount(): Promise<void> {
    try {
      const entries = await getPendingEntries();
      this.updateStatus({ pendingCount: entries.length });
    } catch (error) {
      console.error('Failed to refresh pending count:', error);
    }
  }

  private handleOnline = () => {
    this.updateStatus({ isOnline: true });
    // Delay sync slightly to let connection stabilize
    this.scheduleSyncWithDelay(1000);
  };

  private handleOffline = () => {
    this.updateStatus({ isOnline: false, isSyncing: false });
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
      this.syncTimeoutId = null;
    }
  };

  private scheduleSyncWithDelay(delayMs: number) {
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }
    this.syncTimeoutId = setTimeout(() => {
      this.performSync();
    }, delayMs);
  }

  private async performSync(): Promise<void> {
    if (this.syncInProgress || !this.status.isOnline) {
      return;
    }

    this.syncInProgress = true;
    this.updateStatus({ isSyncing: true, lastError: null });

    try {
      const entries = await getPendingEntries();
      this.updateStatus({ pendingCount: entries.length });

      if (entries.length === 0) {
        this.updateStatus({
          isSyncing: false,
          lastSyncAt: new Date().toISOString(),
        });
        this.syncInProgress = false;
        return;
      }

      // Process entries sequentially to maintain order
      for (const entry of entries) {
        if (!this.status.isOnline) {
          // Connection lost during sync
          break;
        }

        try {
          await this.syncEntry(entry);
          await removePendingEntry(entry.clientEntryId);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          await updatePendingEntry(entry.clientEntryId, {
            attempts: entry.attempts + 1,
            lastError: errorMessage,
          });

          // If max attempts reached, log but continue with other entries
          if (entry.attempts + 1 >= MAX_SYNC_ATTEMPTS) {
            console.error(
              `Entry ${entry.clientEntryId} reached max attempts:`,
              errorMessage
            );
          }
        }
      }

      // Refresh count after sync
      const remaining = await getPendingEntries();
      this.updateStatus({
        isSyncing: false,
        pendingCount: remaining.length,
        lastSyncAt: new Date().toISOString(),
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Sync failed';
      this.updateStatus({
        isSyncing: false,
        lastError: errorMessage,
      });
    } finally {
      this.syncInProgress = false;
    }
  }

  private async syncEntry(entry: PendingTimeEntry): Promise<void> {
    switch (entry.action) {
      case 'start':
        await api.startTask(entry.taskId);
        break;
      case 'stop':
        await api.stopTask(entry.taskId);
        break;
      case 'complete':
        await api.completeTask(entry.taskId);
        break;
    }
  }

  private updateStatus(updates: Partial<SyncStatus>) {
    this.status = { ...this.status, ...updates };
    this.listeners.forEach((cb) => cb(this.status));
  }

  /**
   * Cleanup listeners.
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }
    this.listeners.clear();
  }
}

// Singleton instance
export const syncService = new SyncService();
